/**
 * usePush — React hook for Web Push (VAPID) subscription management.
 *
 * Handles: capability detection, permission prompt, VAPID subscription,
 * server registration, and unsubscribe. iOS 16.4+ requires the site to be
 * installed to the home screen (standalone) before subscribing works.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
};

const arrayBufferToBase64 = (buf) => {
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.byteLength; i++) bin += String.fromCharCode(bytes[i]);
  return window.btoa(bin);
};

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

export function usePush() {
  const supported =
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window;

  // iOS 16.4+ requires standalone (home-screen install) before Push works.
  const iosBlockedUntilInstalled = supported && isIOS() && !isStandalone();

  const [permission, setPermission] = useState(
    supported ? Notification.permission : "unsupported"
  );
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [publicKey, setPublicKey] = useState("");

  // Fetch server status once on mount so we know whether this device is
  // registered even if the browser lost its subscription.
  useEffect(() => {
    if (!supported) return;
    (async () => {
      try {
        const { data } = await api.get("/push/status");
        setSubscribed(!!data.subscribed);
        setPublicKey(data.public_key || "");
      } catch (_) { /* not logged in yet */ }
    })();
  }, [supported]);

  const subscribe = useCallback(async () => {
    if (!supported) return { ok: false, reason: "unsupported" };
    if (iosBlockedUntilInstalled) return { ok: false, reason: "install-first" };
    setLoading(true);
    try {
      // Request permission — must be triggered from a user gesture.
      let perm = Notification.permission;
      if (perm !== "granted") {
        perm = await Notification.requestPermission();
        setPermission(perm);
      }
      if (perm !== "granted") return { ok: false, reason: "denied" };

      // Fetch (or reuse cached) VAPID public key.
      let key = publicKey;
      if (!key) {
        const { data } = await api.get("/push/public-key");
        key = data.public_key;
        setPublicKey(key);
      }

      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(key),
        });
      }

      const raw = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: raw.endpoint,
        p256dh: raw.keys?.p256dh,
        auth: raw.keys?.auth,
        user_agent: navigator.userAgent,
      });
      setSubscribed(true);
      return { ok: true };
    } catch (e) {
      console.warn("push subscribe failed:", e);
      return { ok: false, reason: "error", error: e };
    } finally {
      setLoading(false);
    }
  }, [supported, iosBlockedUntilInstalled, publicKey]);

  const unsubscribe = useCallback(async () => {
    if (!supported) return { ok: false };
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      const endpoint = sub?.endpoint;
      if (sub) await sub.unsubscribe();
      await api.post("/push/unsubscribe", { endpoint: endpoint || "" });
      setSubscribed(false);
      return { ok: true };
    } catch (e) {
      console.warn("push unsubscribe failed:", e);
      return { ok: false, error: e };
    } finally {
      setLoading(false);
    }
  }, [supported]);

  return {
    supported,
    iosBlockedUntilInstalled,
    permission,
    subscribed,
    loading,
    subscribe,
    unsubscribe,
  };
}
